/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable sonarjs/no-duplicate-string */

import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { forkJoin, from, Observable, of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  OidcResult,
  OktaFlowService,
  TokenBody,
} from '@cigna/shared/angular/auth/oidc-util/okta';
import { AuthApiError, urlParamsToObject } from '@okta/okta-auth-js';

import {
  authActions,
  AuthFacade,
} from '@cigna/shared/angular/auth-data-access';

import * as LoginActions from './login.actions';
import { LoginService } from './login.service';
import { MatDialog } from '@angular/material/dialog';

import {
  AuthTransactionMfaChallenge,
  LoginStatus,
  ViewScreen,
  VIEW_SCREEN_URL,
  AuthTransaction,
  SignInFailReason,
  AuthTransactionMfaEnroll,
  AuthTransactionMfaEnrollActivate,
  FactorEnroll,
  AuthTransactionSuccessStepUp,
  AuthTransactionSuccessAuthN,
  LoginClient,
  AuthTransactionUnauthStepUp,
  Factor,
  ResetPasswordFailReason,
  MfaSignOutReason,
} from './login.models';
import { LoginFacade } from './login.facade';
import { Store } from '@ngrx/store';
import { WindowService } from '@cigna/shared/angular/core/window-util';
import { CpiService } from './cpi.service';
import { CookieService } from 'ngx-cookie-service';
import { CustomerService } from './customer.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';
import { JwtFrontendService } from './jwt-frontend.service';
import { UserSessionStorageService } from '@cigna/cigna-for-brokers/shared/util';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { TrackUtil } from '@cigna/shared/angular/analytics-ui';
import { resetState } from './login.actions';
import { AnalyticsService } from '@cigna/shared/angular/analytics-data-access';
import * as uuid from 'uuid';
import { AppstateFacade } from '@cigna/cigna-for-brokers/shell/data-access';
import { AppService } from '@cigna/cigna-for-brokers/shared/data-access';
import { TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MfaTimeoutModalComponent } from '@cigna/cigna-for-brokers/shared/ui';

const LOADING_SPINNER_DELAY_MS = 50;
const UNLINKED_INBOUND_FED_COOKIE = 'INBOUND_FED';
const LINKED_INBOUND_FED_COOKIE = 'INBOUND_FED_DEEPLINK';
const F5_SESSION_COOKIES = ['MRHSession', 'LastMRH_Session'];
const REQUIRED_CLAIM = 'ent.appName';
const SESSION_FINGERPRINT = 'USER_SESSION_FP';

@Injectable()
export class LoginEffects {
  transaction$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.updateTransaction),
        withLatestFrom(this._facade.clientId$),
        tap(
          ([
            { transaction },
            clientId,
            // eslint-disable-next-line sonarjs/cognitive-complexity
          ]) => {
            const { status } = transaction;
            switch (status) {
              case LoginStatus.SUCCESS: {
                if (
                  (transaction as AuthTransactionSuccessStepUp).type ===
                  'SESSION_STEP_UP'
                ) {
                  const { data } = transaction as AuthTransactionSuccessStepUp;
                  this._window.location.assign(data._links.next.href);
                  return;
                }

                if (
                  this._window.location.pathname.includes('public/mfa/enroll')
                ) {
                  const enrolledContact =
                    localStorage.getItem('enrolledContact');
                  const skipFactor = localStorage.getItem('skipFactor');
                  let did0621ChangesEnabled = false;
                  this._features
                    .featuresEnabled(['did0621ChangesEnabled'])
                    .subscribe((isFeatureToggleEnabled) => {
                      did0621ChangesEnabled = isFeatureToggleEnabled;
                    });

                  if (did0621ChangesEnabled) {
                    if (skipFactor !== 'Y') {
                      this._loginService.trackEnrolledContact(enrolledContact);
                    }
                  } else {
                    this._loginService.trackEnrolledContact(enrolledContact);
                  }
                  this.dialogRef.closeAll();
                  return this.navigate(ViewScreen.MfaEnrollWelcome);
                }
                const { user } = transaction as AuthTransactionSuccessAuthN;

                return this._facade.preLoadProfile(user?.profile?.login);
              }

              case LoginStatus.MFA_REQUIRED: {
                // jwt cache warm-up
                // this._jwtFrontend.cacheProfile(user.profile.login).subscribe();

                return this.navigate(ViewScreen.MFA);
              }

              case LoginStatus.MFA_CHALLENGE: {
                return this.navigate(ViewScreen.MfaChallenge);
              }

              case LoginStatus.MFA_ENROLL: {
                const { factors } = transaction as AuthTransactionMfaEnroll;
                const emailFactor = factors.find(
                  (f) => f.factorType === 'email',
                );

                const smsFactor = factors.find((f) => f.factorType === 'sms');

                if (!smsFactor && !emailFactor) {
                  throw new Error('Unknown factor to activate!');
                }
                if (emailFactor?.status === 'ACTIVE') {
                  this.dialogRef.closeAll();
                  this._appStateFacade.sendAnalytics({
                    event: 'pageAction',
                    reqData: {
                      requirementId: 'acCFBMFA010',
                      userStory: 'MTD-2653',
                      hitType: 'action',
                    },
                    actionData: {
                      widgetName: 'email-verify-success',
                      customLinkText: 'Email Verified',
                    },
                  });
                }
                if (smsFactor?.status === 'ACTIVE') {
                  this.dialogRef.closeAll();
                  this._appStateFacade.sendAnalytics({
                    event: 'pageAction',
                    reqData: {
                      requirementId: 'acCFBMFA007',
                      userStory: 'MTD-2650',
                      hitType: 'action',
                    },
                    actionData: {
                      widgetName: 'sms-verify-success',
                      customLinkText: 'SMS Verified',
                    },
                  });
                }

                return this.navigate(ViewScreen.MfaEnrollWelcome);
              }

              case LoginStatus.MFA_ENROLL_ACTIVATE: {
                return this.navigate(ViewScreen.MfaEnrollWelcome);
              }

              case LoginStatus.LOCKED_OUT: {
                return this._store$.dispatch(
                  LoginActions.signInFailed({
                    reason: SignInFailReason.LockedOut,
                  }),
                );
              }
              case LoginStatus.PASSWORD_WARN: {
                return this._store$.dispatch(
                  LoginActions.signInFailed({
                    reason: SignInFailReason.PasswordWarn,
                  }),
                );
              }
              case LoginStatus.PASSWORD_EXPIRED: {
                if (
                  this._window.location.pathname.includes('public/mfa/enroll')
                ) {
                  this.dialogRef.closeAll();
                  return this.navigate(ViewScreen.MfaEnrollWelcome);
                }
                return this._store$.dispatch(
                  LoginActions.signInFailed({
                    reason: SignInFailReason.PasswordExpire,
                  }),
                );
              }

              default:
                return this.navigate(this.loginClientToViewScreen(clientId));
            }
          },
        ),
      ),
    { dispatch: false },
  );

  createSession$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.exchangeToken),
      switchMap(({ sessionToken }) =>
        from(this._authenticator.silentSignIn(sessionToken)).pipe(
          switchMap((auth) => {
            if (!(REQUIRED_CLAIM in auth.accessTokenBody)) {
              return [
                LoginActions.signInFailed({
                  reason: SignInFailReason.InlineHookFailed,
                }),
              ];
            }
            return [
              authActions.loadAuthSuccess({ auth }),
              LoginActions.signInSuccess(),
            ];
          }),
          catchError((e) => this.handleError(e)),
        ),
      ),
    ),
  );

  staySignin$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.staySignIn),
        tap(() => {
          this._authenticator.silentSignIn();
        }),
      ),
    { dispatch: false },
  );

  createSessionPreLoadProfile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.preLoadProfile),
      switchMap(({ ssoId }) =>
        from(this._loginService.preLoadProfile(ssoId)).pipe(
          switchMap(() => [LoginActions.preLoadProfileSuccess()]),
          catchError(
            () => of(LoginActions.preLoadProfileSuccess()),
            // of(LoginActions.preLoadProfileSuccess()),
          ),
        ),
      ),
    ),
  );

  createSessionPreLoadProfileSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.preLoadProfileSuccess),
      withLatestFrom(this._facade.transaction$),
      map(([_, transaction]) =>
        transaction?.sessionToken
          ? LoginActions.exchangeToken({
              sessionToken: transaction.sessionToken,
            })
          : LoginActions.signInFailed({
              reason: SignInFailReason.InlineHookFailed,
            }),
      ),
    ),
  );

  signInSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.signInSuccess),
        withLatestFrom(this._facade.eapToken$, this._facade.isANewUser$),
        tap(() => {
          this.clearClaimsStorage();
          this._cookie.set(
            'OEP_li',
            'true',
            365,
            '/',
            this._window.location.hostname === 'localhost'
              ? 'localhost'
              : '.cigna.com',
            true,
          );
          this._cookie.set(
            SESSION_FINGERPRINT,
            uuid.v4(),
            new Date(Date.now() + 75 * 60 * 1000), // +75 minutes
            '/',
          );
          // this._trackUtil.track('trackFactorVerificationSuccess');
        }),
        switchMap(([_, token, isANewUser]) => {
          // https://confluence.sys.cigna.com/display/CustomerValueStream/SPA+Highlevel+Flow+for+Okta
          const lyncNSync$ = token
            ? this._cpi
                .lyncNSync(token as string)
                .pipe(catchError(() => of(undefined)))
            : of(undefined);

          const fedCookie = this.getIsamCookie(UNLINKED_INBOUND_FED_COOKIE);

          const linkUser$ = fedCookie
            ? this._customer.linkUser(fedCookie).pipe(
                catchError(() => of(undefined)),
                finalize(() => {
                  this.deleteIsamCookie(UNLINKED_INBOUND_FED_COOKIE);
                }),
              )
            : of(undefined);

          return forkJoin([lyncNSync$, linkUser$]).pipe(
            map(([_a, _b]) => ViewScreen.LoginSuccess),
          );
        }),
        tap((nextScreen) => {
          this.navigate(nextScreen);
        }),
      ),
    { dispatch: false },
  );

  scheduleSpinner$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.scheduleLoadingSpinnerWithDelay),
      delay(LOADING_SPINNER_DELAY_MS),
      withLatestFrom(this._facade.loadingSpinnerCount$),
      filter(([_, count]) => count > 0),
      map(() => LoginActions.toggleLoadingSpinner({ isEnabled: true })),
    ),
  );

  signIn$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.signIn),
      withLatestFrom(this._facade.stepupToken$),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      switchMap(([{ options }, stateToken]) =>
        from(
          this._authenticator.signInWithCredentials({
            stateToken,
            options: {
              multiOptionalFactorEnroll: true,
              warnBeforePasswordExpired: true,
            },
            username: options.username,
            password: options.password,
          }),
        ).pipe(
          withLatestFrom(this._facade.clientId$),
          tap(([_, clientId]) => {
            const urlParams: Record<string, string> = urlParamsToObject(
              this._window.location.search,
            );
            const originurl = this._authenticator.getOriginalUri();
            if (urlParams.pagename) {
              this.setFedLocation(urlParams.pagename);
            }
            if (urlParams.redir_page) {
              this._authenticator.setOriginalUri(urlParams.redir_page);
            } else if (originurl) {
              this._facade.setPostloginPopupRedirectUrl(originurl);
              this._authenticator.setOriginalUri(
                '/secure/home/login-success-dialog',
              );
            } else {
              this._authenticator.setOriginalUri(
                '/secure/home/login-success-dialog',
              );
            }
          }),
          map(([transaction]: [AuthTransaction, LoginClient]) =>
            LoginActions.updateTransaction({ transaction }),
          ),
          tap(() => {
            // this will populate eVar84 field in analytics events
            this._analytics.updateStore(
              {
                profileInfo: {
                  ssoId: options.username,
                },
              },
              'user.profile',
            );
          }),
          catchError((e) => this.handleError(e)),
        ),
      ),
    ),
  );

  sendCodeToFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.sendCodeToFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      switchMap(({ factor }) =>
        from(factor.verify()).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e, MfaSignOutReason.TIMEOUT)),
        ),
      ),
    ),
  );

  resendCodeToFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.resendCodeToFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(
        this._facade.transaction$ as Observable<
          AuthTransactionMfaChallenge | AuthTransactionMfaEnrollActivate
        >,
      ),
      switchMap(([, { resend, factor }]) =>
        from(resend(factor.factorType)).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) =>
            this.handleError(e, MfaSignOutReason.RESEND_EXCEED_LIMIT),
          ),
        ),
      ),
    ),
  );

  verifyFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.verifyFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
        const originurl = this._authenticator.getOriginalUri();

        if (originurl) {
          this._authenticator.setOriginalUri(originurl);
        } else {
          this._authenticator.setOriginalUri(
            '/secure/home/login-success-dialog',
          );
        }
        // this._authenticator.setOriginalUri('/secure/home/login-success-dialog');
      }),
      withLatestFrom(
        this._facade.transaction$ as Observable<AuthTransactionMfaChallenge>,
      ),
      switchMap(([{ code, rememberDevice }, { verify }]) =>
        from(verify({ passCode: code, rememberDevice })).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e, MfaSignOutReason.TIMEOUT)),
        ),
      ),
    ),
  );

  enrollEmailFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.enrollEmailFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(
        this._facade.transaction$ as Observable<AuthTransactionMfaEnroll>,
      ),
      switchMap(([_, { factors }]) => {
        const emailFactor = factors.find((f) => f.factorType === 'email');
        if (!emailFactor) {
          throw new Error('Email factor not found!');
        }

        return from(emailFactor.enroll()).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e, MfaSignOutReason.TIMEOUT)),
        );
      }),
    ),
  );

  enrollPhone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.enrollPhoneFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(
        this._facade.transaction$ as Observable<AuthTransactionMfaEnroll>,
      ),
      switchMap(([{ phoneNumber }, { factors }]) => {
        const smsFactor = factors?.find(
          (f) => f.factorType === 'sms',
        ) as FactorEnroll;
        return from(
          smsFactor.enroll({
            profile: {
              updatePhone: true,
              phoneNumber,
            },
          }),
        ).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e, MfaSignOutReason.TIMEOUT)),
        );
      }),
    ),
  );

  skipEnrollFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.skipEnrollFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(
        this._facade.transaction$ as Observable<AuthTransactionMfaEnroll>,
      ),
      switchMap(([, { skip }]) =>
        from(skip()).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e)),
        ),
      ),
    ),
  );

  activateFactor$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.activateFactor),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(
        this._facade
          .transaction$ as Observable<AuthTransactionMfaEnrollActivate>,
        this._facade.isAfterRegistration$,
      ),
      switchMap(([{ code }, { activate, factor }, isAfterRegistration]) =>
        from(activate({ passCode: code })).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e, MfaSignOutReason.TIMEOUT)),
        ),
      ),
    ),
  );

  cancelTransaction$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.cancelTransaction),
      tap(() => {
        if (!this._window.location.pathname.includes('public/registration')) {
          this._facade.scheduleLoadingSpinnerWithDelay();
        }
      }),
      withLatestFrom(this._facade.transaction$),
      switchMap(([_, { cancel }]) =>
        from(cancel()).pipe(
          switchMap((transaction) => [
            LoginActions.updateTransaction({ transaction }),
            LoginActions.resetState(),
          ]),
          catchError((e) => this.handleError(e)),
        ),
      ),
    ),
  );

  prevTransaction$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.prevTransaction),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(this._facade.transaction$),
      switchMap(([_, { prev }]) =>
        from(prev()).pipe(
          map((transaction) => LoginActions.updateTransaction({ transaction })),
          catchError((e) => this.handleError(e)),
        ),
      ),
    ),
  );

  goBack$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.goBack),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      withLatestFrom(this._facade.canGoBack$),
      map(([_, canGoBack]) =>
        canGoBack
          ? LoginActions.prevTransaction()
          : LoginActions.cancelTransaction(),
      ),
    ),
  );

  loginPageVisit$ = createEffect(
    () =>
      this._router.events.pipe(
        filter<NavigationEnd>((e) => e instanceof NavigationEnd),
        withLatestFrom(this._facade.hasTransaction$, this._facade.stepupToken$),
        tap(([e, hasTransaction, stepupToken]) => {
          const clientId = this.viewScreenToLoginClient(e.url.split('?')[0]);
          if (clientId) {
            this._facade.updateLoginClient(clientId);
            this._cookie.delete(SESSION_FINGERPRINT, '/');
            if (hasTransaction && !stepupToken) {
              this._facade.cancelTransaction();
            }
          }
        }),
      ),
    { dispatch: false },
  );

  lockout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.signInFailed),
        filter(({ reason }) => reason === SignInFailReason.LockedOut),
        withLatestFrom(
          this._facade.clientId$,
          this._facade.lockOutLoginRedirectUrl$,
        ),
        tap(([_, clientId, lockOutLoginRedirectUrl]) => {
          this._router.navigate([
            VIEW_SCREEN_URL[this.loginClientToViewScreen(clientId)],
          ]);
          const userErrorMessage = this._translate.instant(
            `loginScreen.LockedOut`,
          );
          const lockedOutError = this._translate.instant(
            `loginScreen.lockedout`,
          );

          if (!lockOutLoginRedirectUrl) {
            this._appStateFacade.sendAnalytics({
              event: 'error',
              reqData: {
                requirementId: 'acCFBLogER001',
                userStory: 'VULCANS-780',
                hitType: 'error',
              },
              errorData: {
                widgetName: 'error-message',
                customLinkText: `${lockedOutError || 'Inline Alert Message'}`,
                errorCause: userErrorMessage,
                errorCode: 'E0000069',
              },
            });
          } else {
            const lockedSource = lockOutLoginRedirectUrl.includes(
              'forgot-username/code-verify',
            )
              ? 'MFA_Username_Too_Many_Attempts'
              : 'MFA_Password_Too_Many_Attempts';

            this._appStateFacade.sendAnalytics({
              event: 'error',
              reqData: {
                requirementId: 'acCFBGlobER001',
                userStory: 'MTD-3249',
                hitType: 'error',
              },
              errorData: {
                errorType: lockedSource,
                errorCause:
                  'You have exceeded the login attempts limit. Please try again in one hour.',
                errorCode: 'E0000069',
              },
            });
          }
        }),
      ),
    { dispatch: false },
  );

  passwordExpire$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.signInFailed),
        filter(({ reason }) => reason === SignInFailReason.PasswordExpire),
        withLatestFrom(this._facade.clientId$),
        tap(([_, clientId]) => {
          this.dialogRef.closeAll();
          this._router.navigate([VIEW_SCREEN_URL[ViewScreen.PasswordExpire]]);
        }),
      ),
    { dispatch: false },
  );

  inlineHookFailed$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.signInFailed),
      filter(({ reason }) => reason === SignInFailReason.InlineHookFailed),
      map(() => resetState()),
      tap(() => {
        this._authenticator.deleteAccessTokenCookie();
        this._router.navigate([VIEW_SCREEN_URL[ViewScreen.Login]]);
        const userErrorMessage = this._translate.instant(
          `loginScreen.InlineHookFailed`,
        );
        const error = this._translate.instant(`loginScreen.signInError`);
        this._appStateFacade.sendAnalytics({
          event: 'error',
          reqData: {
            requirementId: 'acCFBGlobER001',
            userStory: 'MTD-3110',
            hitType: 'error',
          },
          errorData: {
            errorType: 'Login',
            errorCause: `${userErrorMessage} Contact Us`,
            errorCode: '',
          },
        });
      }),
    ),
  );

  violations$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.signInFailed),
      filter(({ reason }) => reason === SignInFailReason.Error403),
      tap(({ errorId }) => {
        this._router.navigate([VIEW_SCREEN_URL[ViewScreen.Error]], {
          queryParams: { error_code: errorId ? errorId : '0x38cD0403' },
          state: { supportId: errorId },
        });
      }),
      map(() => resetState()),
    ),
  );

  connectionReset$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.signInFailed),
      filter(({ reason }) => reason === SignInFailReason.ConnectionReset),
      tap(() => {
        this._router.navigate([VIEW_SCREEN_URL[ViewScreen.Error]], {
          queryParams: { error_code: '0x38cE0000' },
        });
      }),
      map(() => resetState()),
    ),
  );

  accessDenied$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.signInFailed),
        filter(({ reason }) => reason === SignInFailReason.AccessDenied),
        tap(() => {
          this._authenticator.deleteAccessTokenCookie();
          this.deleteF5Cookies();
          this._router.navigate([VIEW_SCREEN_URL[ViewScreen.Error]], {
            queryParams: { error_code: '0x38cf0427' },
          });
        }),
      ),
    { dispatch: false },
  );

  tokenExpiration$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.signInFailed),
      filter(({ reason }) => reason === SignInFailReason.TokenExpired),
      map(() => LoginActions.loginTimeout()),
    ),
  );

  loginTimeout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.loginTimeout),
        tap(() =>
          this._router.navigate([VIEW_SCREEN_URL[ViewScreen.Login]], {
            queryParams: { userstatus: 'timeout' },
          }),
        ),
      ),
    { dispatch: false },
  );

  impersonate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.impersonate),
      switchMap(() => {
        const urlParams: Record<string, string> = urlParamsToObject(
          this._window.location.hash,
        );
        const storage = this._authenticator.getTransactionStorage();
        storage.setStorage({ state: urlParams.state, responseType: 'code' });
        return from(
          this._authenticator.parseTokensFromUrl({ responseMode: 'fragment' }),
        );
      }),
      map((tokens) => {
        if (!tokens.accessToken) {
          throw Error(`Missing access token`);
        }

        return LoginActions.impersonateSuccess({
          accessToken: tokens.accessToken,
        });
      }),
      catchError((e) => this.handleError(e)),
    ),
  );

  impersonateSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.impersonateSuccess),
        tap(({ accessToken }) => {
          if (
            Array.isArray(accessToken.claims?.scp) &&
            accessToken.claims?.scp.includes('impersonation')
          ) {
            this._authenticator.setCookieStorage(accessToken.accessToken);
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            this._auth.loadAuthSuccess({
              accessToken: accessToken.accessToken,
              accessTokenBody: accessToken.claims as TokenBody,
            } as OidcResult);
            this.deleteF5Cookies();
            this._router.navigate(['/secure/home']);
          } else {
            throw Error(`Access token scope validtion failed`);
          }
        }),
      ),
    { dispatch: false },
  );

  federate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.federate),
        tap(() => {
          const urlParams: Record<string, string> = urlParamsToObject(
            this._window.location.search,
          );
          if (urlParams.pagename) {
            this.setFedLocation(urlParams.pagename);
          } else if (this.getIsamCookie(LINKED_INBOUND_FED_COOKIE)) {
            this.setFedLocation(this.getIsamCookie(LINKED_INBOUND_FED_COOKIE));
            this.deleteIsamCookie(LINKED_INBOUND_FED_COOKIE);
          } else {
            this._authenticator.setOriginalUri('');
          }
          this._authenticator.federate();
        }),
      ),
    { dispatch: false },
  );

  stepUp$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.stepup),
      switchMap(() => {
        const urlParams: Record<string, string> = urlParamsToObject(
          this._window.location.search,
        );
        return this._authenticator.introspect(urlParams.token).pipe(
          switchMap((transaction: AuthTransactionUnauthStepUp) => {
            if (
              transaction.status === LoginStatus.UNAUTHENTICATED &&
              transaction.type === 'SESSION_STEP_UP'
            ) {
              // https://confluence.sys.cigna.com/display/CustomerValueStream/Login+Flow+-+Okta+Widget+to+SPA
              const data = transaction.authentication;

              if (data.protocol === 'OAUTH2.0') {
                return [
                  LoginActions.introspectSuccess({
                    token: transaction.data.stateToken,
                  }),
                  LoginActions.updateTransaction({ transaction }),
                ];
              }
            }
            return of(LoginActions.updateTransaction({ transaction }));
          }),
          catchError((e) => this.handleError(e)),
        );
      }),
    ),
  );

  logout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.logout),
        tap(() => {
          this.processLogout();
          this._authenticator.signOut({ userstatus: 'logoff' });
        }),
      ),
    { dispatch: false },
  );

  impersonatorLogout$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.impersonatorLogout),
        tap(() => {
          this.processLogout();

          const step = localStorage.getItem('redirectfromcfb');

          if (step && step === 'true') {
            this._authenticator.signOut({ userstatus: 'logoff' });
          }
        }),
      ),
    { dispatch: false },
  );

  acceptNewUserAgreement$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.acceptNewUserAgreement),
        withLatestFrom(this._facade.userAgreementVersion$),
        switchMap(([_, version]) => {
          if (version) {
            return this._customer.acceptNewUserAgreement(version);
          }
          return of(undefined);
        }),
        finalize(() => {
          this.navigate(ViewScreen.LoginSuccess);
        }),
      ),
    { dispatch: false },
  );

  resetPassword$ = createEffect(() =>
    this._actions$.pipe(
      ofType(LoginActions.resetPassword),
      withLatestFrom(this._facade.transaction$),
      tap(() => {
        this._facade.scheduleLoadingSpinnerWithDelay();
      }),
      switchMap(([{ options }, transaction]) =>
        this._authenticator
          .resetPassword({
            stateToken: (transaction as any).data.stateToken,
            oldPassword: options.oldPassword,
            newPassword: options.newPassword,
          })
          .pipe(
            distinctUntilChanged(),
            switchMap((response: AuthTransaction) => {
              const actions = [];
              if (response && response.status === 'SUCCESS') {
                actions.push(LoginActions.resetPasswordSuccess());
              }

              return actions;
            }),
            catchError((e) => this.handleErrorOnResetPassword(e)),
          ),
      ),
    ),
  );

  resetPasswordSuccess$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.resetPasswordSuccess),
        tap(() => {
          this._appService.setfpStatusToLogin(true);
          this._router.navigateByUrl(VIEW_SCREEN_URL[ViewScreen.Login], {
            state: { isFgPwdSuccess: true, userId: '' },
          });
        }),
      ),
    { dispatch: false },
  );

  mfaTimeOut$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.mfaTimeOut),
        tap(() => this.mfaSessionEndModal(MfaSignOutReason.TIMEOUT)),
      ),
    { dispatch: false },
  );

  mfaResendExceedLimt$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.mfaResendExceedLimit),
        tap(() =>
          this.mfaSessionEndModal(MfaSignOutReason.RESEND_EXCEED_LIMIT),
        ),
      ),
    { dispatch: false },
  );

  mfaCodeExceedLimt$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.mfaCodeExceedLimit),
        tap(() => this.mfaSessionEndModal(MfaSignOutReason.CODE_EXCEED_LIMIT)),
      ),
    { dispatch: false },
  );

  constructor(
    private _store$: Store,
    private _facade: LoginFacade,
    private _router: Router,
    private _actions$: Actions,
    private _auth: AuthFacade,
    private _window: WindowService,
    private _authenticator: OktaFlowService,
    private _cpi: CpiService,
    private _customer: CustomerService,
    private _cookie: CookieService,
    private _features: FeatureTogglesFacade,
    private _jwtFrontend: JwtFrontendService,
    private _userSessionStorageService: UserSessionStorageService,
    private _trackUtil: TrackUtil,
    private _analytics: AnalyticsService,
    private _appStateFacade: AppstateFacade,
    private _appService: AppService,
    private _translate: TranslateService,
    private dialogRef: MatDialog,
    private _loginService: LoginService,
  ) {}

  private processLogout() {
    const TOKEN_COOKIE = 'accessToken';

    this.deleteF5Cookies();
    this._cookie.delete(SESSION_FINGERPRINT, '/');
    this._authenticator.setOriginalUri('');
    window.localStorage.setItem('logout-event', Math.random().toString());

    // Remove trace id
    if (sessionStorage.getItem('cigna-public-trace-id') !== null) {
      sessionStorage.removeItem('cigna-public-trace-id');
    }

    if (localStorage.getItem('cigna-secure-trace-id') !== null) {
      localStorage.removeItem('cigna-secure-trace-id');
    }

    if (sessionStorage.getItem('userPeid') !== null) {
      sessionStorage.removeItem('userPeid');
    }

    const step = localStorage.getItem('redirectfromcfb');

    if (step && step === 'true') {
      this._cookie.delete(TOKEN_COOKIE, '/', this.getCookieDomain());
    } else {
      this._authenticator.signOut({ userstatus: 'logoff' });
      this._router.navigate(['/public/logout']);
    }
  }

  getCookieDomain() {
    return undefined;
  }

  private navigate(screen: ViewScreen) {
    if (screen !== ViewScreen.LoginSuccess) {
      this._facade.disableLoadingSpinner();
    }
    this._router.navigate([VIEW_SCREEN_URL[screen]]);
  }

  public handleError(e: AuthApiError, flowType?: string) {
    const { errorCode } = e;
    const errId = e.errorId;

    const codes: { [code: string]: SignInFailReason } = {
      0: SignInFailReason.Unknown,
      BLOCK0001: SignInFailReason.Error403,
      ERROR000: SignInFailReason.ConnectionReset,
      E0000004: SignInFailReason.InvalidCredentials,
      E0000007: SignInFailReason.NotFound,
      E0000068: SignInFailReason.VerifyFailed,
      E0000011: SignInFailReason.TokenExpired,
      E0000069: SignInFailReason.LockedOut,
      E0000150: SignInFailReason.SmsLimit,
      E0000109: SignInFailReason.SmsAlreadySent,
      X0000109: SignInFailReason.PasswordWarn,
      XX000109: SignInFailReason.PasswordExpire,
      access_denied: SignInFailReason.AccessDenied,
      E0000047: SignInFailReason.ExceedLimit,
      E0000001: SignInFailReason.InvalidMobile,
      E0000082: SignInFailReason.VerifyFailed,
      E0000118: SignInFailReason.EmailPasscodeAlreadySent,
      E0000119: SignInFailReason.TooManyRequest,
      E0000120: SignInFailReason.TimeOut,
    };

    const reasonCode = codes[errorCode ?? 0] ?? SignInFailReason.Unknown;

    const userErrorMessage = this._translate.instant(
      `loginScreen.${SignInFailReason[reasonCode]}`,
    );

    // track analytics here
    if (
      errorCode !== 'E0000109' &&
      errorCode !== 'E0000068' &&
      errorCode !== 'E0000118' &&
      errorCode !== 'E0000047' &&
      errorCode !== 'E0000069'
    ) {
      this._appStateFacade.sendAnalytics({
        event: 'error',
        reqData: {
          requirementId: 'acCFBLogER001',
          userStory: 'VULCANS-780',
          hitType: 'error',
        },
        errorData: {
          widgetName: 'error-message',
          customLinkText: `${e.errorSummary || 'Inline Alert Message'}`,
          errorCause: `${userErrorMessage} contact us.`,
          errorCode: e.errorCode ?? '',
        },
      });
    }

    if (errorCode === 'E0000109') {
      this._appStateFacade.sendAnalytics({
        event: 'error',
        reqData: {
          requirementId: 'acCFBGlobER001',
          userStory: 'MTD-3250',
          hitType: 'error',
        },
        errorData: {
          errorType: 'MFA_Second_Request',
          errorCause:
            'A security code was recently sent.  Please wait 30 seconds before trying again.',
          errorCode: 'E0000109',
        },
      });
    }

    if (errorCode === 'E0000047' || errorCode === 'E0000069') {
      if (flowType === MfaSignOutReason.RESEND_EXCEED_LIMIT) {
        return of(LoginActions.mfaResendExceedLimit());
      }
      if (flowType === MfaSignOutReason.TIMEOUT) {
        return of(LoginActions.mfaCodeExceedLimit());
      }
    }
    if (errorCode === 'E0000011' && flowType === MfaSignOutReason.TIMEOUT) {
      return of(LoginActions.mfaTimeOut());
    }
    return of(
      LoginActions.signInFailed({
        reason: reasonCode,
        errorId: errId,
      }),
    );
  }

  // eslint-disable-next-line sonarjs/no-identical-functions
  public handleErrorOnResetPassword(e: AuthApiError) {
    const { errorCode } = e;
    const errorCauses = e.errorCauses as any;
    let failedReason: number = ResetPasswordFailReason.UpdateFailed;
    if (errorCode === 'E0000014' && errorCauses && errorCauses.length > 0) {
      switch (errorCauses[0].errorSummary) {
        case 'Old Password is not correct':
          failedReason = ResetPasswordFailReason.OldPasswordError;
          break;
        case 'Password has been used too recently':
          failedReason = ResetPasswordFailReason.RecentPasswordError;
          break;
        case 'Password cannot be your current password':
          failedReason = ResetPasswordFailReason.UpdateCurrentPasswordFailed;
          break;
        default:
          failedReason = ResetPasswordFailReason.UpdateFailed;
      }
    }

    const codes: { [code: string]: ResetPasswordFailReason } = {
      0: ResetPasswordFailReason.Unknown,
      E0000011: ResetPasswordFailReason.TokenExpired,
      E0000014: failedReason,
    };

    const reasonCode = codes[errorCode ?? 0] ?? ResetPasswordFailReason.Unknown;

    const userErrorMessage = this._translate.instant(
      `resetPassword.${ResetPasswordFailReason[reasonCode]}`,
    );

    this._appStateFacade.sendAnalytics({
      event: 'error',
      reqData: {
        requirementId: 'acCFBLogER001',
        userStory: 'VULCANS-780',
        hitType: 'error',
      },
      errorData: {
        widgetName: 'error-message',
        customLinkText: `${e.errorSummary || 'Inline Alert Message'}`,
        errorCause: userErrorMessage,
        errorCode: e.errorCode ?? '',
      },
    });

    return of(
      LoginActions.resetPasswordFailed({
        reason: reasonCode,
      }),
    );
  }

  mfaSessionEndModal = (reason: string) => {
    this.dialogRef.closeAll();

    if (reason === MfaSignOutReason.TIMEOUT) {
      this._appStateFacade.sendAnalytics({
        event: 'error',
        reqData: {
          requirementId: 'acCFBGlobER001',
          userStory: 'MTD-3250',
          hitType: 'error',
        },
        errorData: {
          errorType: 'MFA_Timeout',
          errorCause:
            'Your session has timed out. For your security, we end your session after five minutes of inactivity.',
          errorCode: 'E0000120',
        },
      });
    } else if (reason === MfaSignOutReason.RESEND_EXCEED_LIMIT) {
      this._appStateFacade.sendAnalytics({
        event: 'error',
        reqData: {
          requirementId: 'acCFBGlobER001',
          userStory: 'MTD-3250',
          hitType: 'error',
        },
        errorData: {
          errorType: 'MFA_Too_Many_Requests',
          errorCause:
            'Your session has ended. For your security, we end your session after you have entered an incorrect security code too many times.',
          errorCode: 'E0000047',
        },
      });
    } else if (reason === MfaSignOutReason.CODE_EXCEED_LIMIT) {
      this._appStateFacade.sendAnalytics({
        event: 'error',
        reqData: {
          requirementId: 'acCFBGlobER001',
          userStory: 'MTD-3250',
          hitType: 'error',
        },
        errorData: {
          errorType: 'MFA_Too_Many_Requests',
          errorCause:
            'Your session has ended. For your security, we end your session after you have entered an incorrect security code too many times.',
          errorCode: 'E0000119',
        },
      });
    }

    const timeoutDialogRef = this.dialogRef.open(MfaTimeoutModalComponent, {
      width: '50rem',
      autoFocus: false,
      disableClose: true,
      data: reason,
    });
    timeoutDialogRef.afterClosed().subscribe(() => {
      this._appStateFacade.setLastVisitedUser(undefined);
      this._facade.cancelTransaction();
      this._router.navigate(['/public/login']);
    });
  };

  private setFedLocation(fedkey: string) {
    this._authenticator.setOriginalUri(
      `/web/secure/router/v2/redirect?key=${fedkey}`,
    );
  }

  private loginClientToViewScreen(clientId: LoginClient): ViewScreen {
    return ViewScreen.Login;
  }

  private viewScreenToLoginClient(url: string): LoginClient | undefined {
    if (url === VIEW_SCREEN_URL[ViewScreen.Login]) {
      return LoginClient.CignaForBrokers;
    }
  }

  private clearClaimsStorage() {
    this._userSessionStorageService.clearClaimsStoredKeys();
  }

  private getIsamCookie(name: string) {
    return (
      this._cookie.get(name) || this._cookie.get(this.getIsamCookieName(name))
    );
  }

  private deleteIsamCookie(name: string) {
    this._cookie.delete(name, '/');
    this._cookie.delete(this.getIsamCookieName(name), '/');
  }

  private getIsamCookieName(name: string): string {
    return `AMWEBJCT!/corp/sso!${name}`;
  }

  private deleteF5Cookies() {
    F5_SESSION_COOKIES.forEach((x) => this._cookie.delete(x, '/'));
  }

  private getVerifiedFactors<F extends Factor>(factors: F[]) {
    return factors.filter(
      (f) =>
        f.factorType === 'sms' ||
        (f.factorType === 'email' &&
          !f.profile.email?.endsWith('@login.my.cigna.com')),
    );
  }
}
